import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import {
  ArrowLeft, Mail, Phone, Calendar, DollarSign, UserCheck, FileText,
  Bell, ShoppingBag, Loader2, RefreshCw, Plus, Pencil, Trash2, AlertCircle, CheckCircle2,
  CreditCard, Ticket,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiCall, formatCents, dollarsToCents, type MemberProfile, type MemberStatus } from '@/lib/api';
import { usePermission } from '@/hooks/usePermissions';

type Tab = 'overview' | 'payments' | 'checkins' | 'classes' | 'waivers' | 'store' | 'notifications' | 'billing';

export default function MemberDetail() {
  const { id } = useParams<{ id: string }>();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<MemberProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [editingMember, setEditingMember] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const r = await apiCall<MemberProfile>(`/v1/members/${id}/profile`, { bearerToken: token });
      setData(r);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [getToken, id]);

  useEffect(() => { void load(); }, [load]);

  async function deleteMember() {
    if (!data || !confirm(`Delete ${data.member.name}? This removes their payments, check-ins, and class history.`)) return;
    const token = await getToken();
    await apiCall(`/v1/members/${data.member.id}`, { method: 'DELETE', bearerToken: token });
    navigate('/dashboard/members');
  }

  if (loading && !data) {
    return <div className="py-12 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;
  }
  if (err || !data) {
    return (
      <div className="space-y-3">
        <Button variant="ghost" size="sm" asChild><Link to="/dashboard/members"><ArrowLeft className="mr-1 h-4 w-4" /> Members</Link></Button>
        <Card><CardContent className="py-6 text-sm text-destructive">{err ?? 'Member not found.'}</CardContent></Card>
      </div>
    );
  }

  const m = data.member;
  const owed = !m.is_paid_this_month && m.monthly_dues_cents > 0;
  const totalPaid = data.payments.reduce((s, p) => s + p.amount_cents, 0);
  const checkinsLast30 = data.checkins.filter((c) => Date.now() - new Date(c.checked_in_at.replace(' ', 'T') + 'Z').getTime() < 30 * 86400_000).length;
  const hasWaiver = data.waiver_signatures.length > 0;
  const onVisitPlan = m.balance_days !== null;
  const lowBalance = onVisitPlan && (m.balance_days as number) <= 2;
  const noBalance = onVisitPlan && (m.balance_days as number) <= 0;

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Top bar */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" asChild className="-ml-2"><Link to="/dashboard/members"><ArrowLeft className="mr-1 h-4 w-4" /> Members</Link></Button>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold">{m.name}</h1>
            <StatusBadge status={m.status} />
            {onVisitPlan && (
              <Badge variant={noBalance ? 'destructive' : lowBalance ? 'outline' : 'default'} className="gap-1">
                {m.balance_days} day{m.balance_days === 1 ? '' : 's'} remaining
              </Badge>
            )}
            {owed && <Badge variant="destructive" className="gap-1"><AlertCircle className="h-3 w-3" /> Owes {formatCents(m.monthly_dues_cents - m.paid_this_month_cents)}</Badge>}
            {!hasWaiver && <Badge variant="outline" className="gap-1 text-amber-700"><FileText className="h-3 w-3" /> No waiver</Badge>}
          </div>
          <div className="text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
            {m.email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {m.email}</span>}
            {m.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {m.phone}</span>}
            <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Joined {new Date(m.joined_at.replace(' ', 'T') + 'Z').toLocaleDateString()}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEditingMember(true)}><Pencil className="mr-1 h-4 w-4" /> Edit</Button>
          <Button size="sm" onClick={() => setRecordingPayment(true)}><Plus className="mr-1 h-4 w-4" /> Record payment</Button>
          <Button variant="ghost" size="sm" onClick={() => void deleteMember()} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Stat row */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {onVisitPlan ? (
          <Card><CardHeader className="pb-2"><CardDescription className="flex items-center gap-2"><DollarSign className="h-4 w-4" /> Days remaining</CardDescription>
            <CardTitle className={'text-2xl ' + (noBalance ? 'text-destructive' : lowBalance ? 'text-amber-600' : '')}>{m.balance_days}</CardTitle>
          </CardHeader><CardContent className="text-xs text-muted-foreground">
            {m.days_used} of {m.days_purchased} used
          </CardContent></Card>
        ) : (
          <Card><CardHeader className="pb-2"><CardDescription className="flex items-center gap-2"><DollarSign className="h-4 w-4" /> Monthly dues</CardDescription>
            <CardTitle className="text-2xl">{formatCents(m.monthly_dues_cents)}</CardTitle>
          </CardHeader><CardContent className="text-xs text-muted-foreground">
            {m.is_paid_this_month ? <span className="text-green-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Paid this month</span> : owed ? 'Unpaid for current month' : 'No dues set'}
          </CardContent></Card>
        )}
        <Card><CardHeader className="pb-2"><CardDescription>Total paid (lifetime)</CardDescription>
          <CardTitle className="text-2xl">{formatCents(totalPaid)}</CardTitle>
        </CardHeader><CardContent className="text-xs text-muted-foreground">{data.payments.length} payment{data.payments.length === 1 ? '' : 's'}</CardContent></Card>
        <Card><CardHeader className="pb-2"><CardDescription className="flex items-center gap-2"><UserCheck className="h-4 w-4" /> Check-ins (30d)</CardDescription>
          <CardTitle className="text-2xl">{checkinsLast30}</CardTitle>
        </CardHeader><CardContent className="text-xs text-muted-foreground">{data.checkins.length} total recorded</CardContent></Card>
        <Card><CardHeader className="pb-2"><CardDescription>Class registrations</CardDescription>
          <CardTitle className="text-2xl">{data.class_registrations.length}</CardTitle>
        </CardHeader><CardContent className="text-xs text-muted-foreground">{data.class_registrations.filter((r) => r.status === 'attended').length} attended</CardContent></Card>
      </div>

      {/* Tabs */}
      <div className="border-b flex items-center gap-1 overflow-x-auto">
        {(['overview', 'payments', 'checkins', 'classes', 'waivers', 'store', 'notifications', 'billing'] as Tab[]).map((t) => (
          <button key={t} onClick={() => setTab(t)} className={
            'px-3 py-2 text-sm border-b-2 -mb-px capitalize whitespace-nowrap ' +
            (tab === t ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')
          }>{t}</button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab data={data} />}
      {tab === 'payments' && <PaymentsTab payments={data.payments} onChange={load} />}
      {tab === 'checkins' && <CheckinsTab checkins={data.checkins} />}
      {tab === 'classes' && <ClassesTab regs={data.class_registrations} />}
      {tab === 'waivers' && <WaiversTab sigs={data.waiver_signatures} />}
      {tab === 'store' && <StoreTab sales={data.store_sales} />}
      {tab === 'notifications' && <NotificationsTab notifs={data.notifications} />}
      {tab === 'billing' && <BillingTab subs={data.subscriptions} passes={data.class_passes} memberId={m.id} onChange={load} />}

      {m.notes && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Notes</CardTitle></CardHeader>
          <CardContent><p className="text-sm whitespace-pre-wrap">{m.notes}</p></CardContent>
        </Card>
      )}

      {recordingPayment && (
        <RecordPaymentDialog
          member={m}
          onClose={() => setRecordingPayment(false)}
          onSaved={() => { setRecordingPayment(false); void load(); }}
        />
      )}
      {editingMember && (
        <EditMemberDialog
          member={m}
          onClose={() => setEditingMember(false)}
          onSaved={() => { setEditingMember(false); void load(); }}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: MemberStatus }) {
  const map = { active: 'default', suspended: 'destructive', inactive: 'outline' } as const;
  return <Badge variant={map[status]} className="capitalize">{status}</Badge>;
}

function OverviewTab({ data }: { data: MemberProfile }) {
  const upcoming = data.class_registrations
    .filter((r) => new Date(r.starts_at.replace(' ', 'T')).getTime() >= Date.now() && r.status !== 'cancelled')
    .slice(0, 5);
  const recentPay = data.payments.slice(0, 5);
  const recentCheckin = data.checkins.slice(0, 5);
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><DollarSign className="h-4 w-4" /> Recent payments</CardTitle></CardHeader>
        <CardContent>
          {recentPay.length === 0 ? <EmptyHint text="No payments yet." /> : (
            <ul className="divide-y">
              {recentPay.map((p) => (
                <li key={p.id} className="flex items-center justify-between py-2 text-sm">
                  <div>
                    <div className="font-medium tabular-nums">{formatCents(p.amount_cents)}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(p.paid_at.replace(' ', 'T') + 'Z').toLocaleDateString()}
                      {p.covers_month && ` · covers ${p.covers_month}`}
                    </div>
                  </div>
                  <Badge variant="outline" className="capitalize">{p.method}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4" /> Upcoming classes</CardTitle></CardHeader>
        <CardContent>
          {upcoming.length === 0 ? <EmptyHint text="Not registered for any upcoming sessions." /> : (
            <ul className="divide-y">
              {upcoming.map((r) => (
                <li key={r.id} className="py-2 text-sm">
                  <div className="font-medium">{r.class_name}</div>
                  <div className="text-xs text-muted-foreground">{new Date(r.starts_at.replace(' ', 'T')).toLocaleString()}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><UserCheck className="h-4 w-4" /> Recent check-ins</CardTitle></CardHeader>
        <CardContent>
          {recentCheckin.length === 0 ? <EmptyHint text="Never checked in." /> : (
            <ul className="divide-y">
              {recentCheckin.map((c) => (
                <li key={c.id} className="flex items-center justify-between py-2 text-sm">
                  <span className="text-xs text-muted-foreground tabular-nums">{new Date(c.checked_in_at.replace(' ', 'T') + 'Z').toLocaleString()}</span>
                  <Badge variant="outline" className="capitalize">{c.source}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Waiver status</CardTitle></CardHeader>
        <CardContent>
          {data.waiver_signatures.length === 0 ? (
            <div className="text-sm space-y-2">
              <div className="text-amber-700 flex items-center gap-1.5"><AlertCircle className="h-4 w-4" /> No waiver on file.</div>
              <p className="text-xs text-muted-foreground">Send this member the public waiver link to sign.</p>
              <Button asChild size="sm" variant="outline"><Link to="/waiver">Open public waiver</Link></Button>
            </div>
          ) : (
            <ul className="divide-y">
              {data.waiver_signatures.map((s) => (
                <li key={s.id} className="py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="font-medium">{s.waiver_title} v{s.waiver_version}</span>
                  </div>
                  <div className="text-xs text-muted-foreground ml-6">Signed {new Date(s.signed_at.replace(' ', 'T') + 'Z').toLocaleString()}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PaymentsTab({ payments, onChange }: { payments: MemberProfile['payments']; onChange: () => void }) {
  const { getToken } = useAuth();
  const canRefund = usePermission('payments:refund');
  const [refunding, setRefunding] = useState<string | null>(null);

  async function refund(id: string) {
    const reason = prompt('Refund reason (optional, shown to staff only):') ?? '';
    if (!confirm('Issue a refund? For card payments this calls Stripe. For cash, a negative journal-entry payment is created.')) return;
    setRefunding(id);
    try {
      const token = await getToken();
      await apiCall(`/v1/stripe/payments/${id}/refund`, { method: 'POST', bearerToken: token, body: { reason } });
      onChange();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Refund failed');
    } finally { setRefunding(null); }
  }

  if (payments.length === 0) return <EmptyCard icon={<DollarSign className="h-8 w-8" />} text="No payments recorded." />;
  return (
    <Card><CardContent className="p-0"><table className="w-full text-sm">
      <thead className="text-xs text-muted-foreground border-b"><tr>
        <th className="text-left px-4 py-2 font-medium">When</th>
        <th className="text-right px-4 py-2 font-medium">Amount</th>
        <th className="text-right px-4 py-2 font-medium">Days</th>
        <th className="text-left px-4 py-2 font-medium">Method</th>
        <th className="text-left px-4 py-2 font-medium">Covers</th>
        <th className="text-left px-4 py-2 font-medium">Notes</th>
        <th className="px-4 py-2"></th>
      </tr></thead>
      <tbody>{payments.map((p) => {
        const isRefund = (p.notes ?? '').includes('refund:');
        const refunded = (p.notes ?? '').match(/refund:re_/);
        return (
          <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
            <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">{new Date(p.paid_at.replace(' ', 'T') + 'Z').toLocaleString()}</td>
            <td className={'px-4 py-2 text-right font-medium tabular-nums ' + (p.amount_cents < 0 ? 'text-destructive' : '')}>{formatCents(p.amount_cents)}</td>
            <td className="px-4 py-2 text-right tabular-nums">{p.days_added ?? <span className="text-muted-foreground">—</span>}</td>
            <td className="px-4 py-2"><Badge variant="outline" className="capitalize">{p.method}</Badge></td>
            <td className="px-4 py-2 text-xs">{p.covers_month ?? <span className="text-muted-foreground">—</span>}</td>
            <td className="px-4 py-2 text-xs text-muted-foreground truncate max-w-xs">{p.notes ?? ''}</td>
            <td className="px-4 py-2 text-right">
              {p.amount_cents > 0 && !isRefund && !refunded && canRefund && (
                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-muted-foreground hover:text-destructive" onClick={() => void refund(p.id)} disabled={refunding === p.id}>
                  {refunding === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refund'}
                </Button>
              )}
            </td>
          </tr>
        );
      })}</tbody>
    </table></CardContent></Card>
  );
}

function CheckinsTab({ checkins }: { checkins: MemberProfile['checkins'] }) {
  if (checkins.length === 0) return <EmptyCard icon={<UserCheck className="h-8 w-8" />} text="Never checked in." />;
  return (
    <Card><CardContent className="p-0"><table className="w-full text-sm">
      <thead className="text-xs text-muted-foreground border-b"><tr>
        <th className="text-left px-4 py-2 font-medium">When</th>
        <th className="text-left px-4 py-2 font-medium">Source</th>
        <th className="text-left px-4 py-2 font-medium">Notes</th>
      </tr></thead>
      <tbody>{checkins.map((c) => (
        <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
          <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">{new Date(c.checked_in_at.replace(' ', 'T') + 'Z').toLocaleString()}</td>
          <td className="px-4 py-2"><Badge variant="outline" className="capitalize">{c.source}</Badge></td>
          <td className="px-4 py-2 text-xs text-muted-foreground">{c.notes ?? ''}</td>
        </tr>
      ))}</tbody>
    </table></CardContent></Card>
  );
}

function ClassesTab({ regs }: { regs: MemberProfile['class_registrations'] }) {
  if (regs.length === 0) return <EmptyCard icon={<Calendar className="h-8 w-8" />} text="No class registrations." />;
  return (
    <Card><CardContent className="p-0"><table className="w-full text-sm">
      <thead className="text-xs text-muted-foreground border-b"><tr>
        <th className="text-left px-4 py-2 font-medium">Class</th>
        <th className="text-left px-4 py-2 font-medium">When</th>
        <th className="text-left px-4 py-2 font-medium">Status</th>
      </tr></thead>
      <tbody>{regs.map((r) => (
        <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
          <td className="px-4 py-2 font-medium">{r.class_name}</td>
          <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">{new Date(r.starts_at.replace(' ', 'T')).toLocaleString()}</td>
          <td className="px-4 py-2"><Badge variant={r.status === 'attended' ? 'default' : r.status === 'no_show' ? 'destructive' : 'outline'} className="capitalize">{r.status.replace('_', ' ')}</Badge></td>
        </tr>
      ))}</tbody>
    </table></CardContent></Card>
  );
}

function WaiversTab({ sigs }: { sigs: MemberProfile['waiver_signatures'] }) {
  if (sigs.length === 0) return <EmptyCard icon={<FileText className="h-8 w-8" />} text="No signed waivers." />;
  return (
    <div className="space-y-3">
      {sigs.map((s) => (
        <Card key={s.id}><CardContent className="py-4 flex items-center justify-between">
          <div>
            <div className="font-medium flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> {s.waiver_title} v{s.waiver_version}</div>
            <div className="text-xs text-muted-foreground ml-6">Signed by {s.signer_name} · {new Date(s.signed_at.replace(' ', 'T') + 'Z').toLocaleString()}</div>
            {s.ip_address && <div className="text-xs text-muted-foreground ml-6 font-mono">IP {s.ip_address}</div>}
          </div>
        </CardContent></Card>
      ))}
    </div>
  );
}

function StoreTab({ sales }: { sales: MemberProfile['store_sales'] }) {
  if (sales.length === 0) return <EmptyCard icon={<ShoppingBag className="h-8 w-8" />} text="No store purchases linked to this member." />;
  return (
    <Card><CardContent className="p-0"><table className="w-full text-sm">
      <thead className="text-xs text-muted-foreground border-b"><tr>
        <th className="text-left px-4 py-2 font-medium">When</th>
        <th className="text-left px-4 py-2 font-medium">Product</th>
        <th className="text-right px-4 py-2 font-medium">Qty</th>
        <th className="text-right px-4 py-2 font-medium">Total</th>
        <th className="text-left px-4 py-2 font-medium">Method</th>
      </tr></thead>
      <tbody>{sales.map((s) => (
        <tr key={s.id} className="border-b last:border-0 hover:bg-muted/40">
          <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">{new Date(s.sold_at.replace(' ', 'T') + 'Z').toLocaleString()}</td>
          <td className="px-4 py-2">{s.product_name}</td>
          <td className="px-4 py-2 text-right tabular-nums">{s.qty}</td>
          <td className="px-4 py-2 text-right font-medium tabular-nums">{formatCents(s.total_cents)}</td>
          <td className="px-4 py-2"><Badge variant="outline" className="capitalize">{s.payment_method}</Badge></td>
        </tr>
      ))}</tbody>
    </table></CardContent></Card>
  );
}

function NotificationsTab({ notifs }: { notifs: MemberProfile['notifications'] }) {
  if (notifs.length === 0) return <EmptyCard icon={<Bell className="h-8 w-8" />} text="No notifications sent to this member." />;
  return (
    <Card><CardContent className="p-0"><table className="w-full text-sm">
      <thead className="text-xs text-muted-foreground border-b"><tr>
        <th className="text-left px-4 py-2 font-medium">When</th>
        <th className="text-left px-4 py-2 font-medium">Channel</th>
        <th className="text-left px-4 py-2 font-medium">Template</th>
        <th className="text-left px-4 py-2 font-medium">Recipient</th>
        <th className="text-left px-4 py-2 font-medium">Status</th>
      </tr></thead>
      <tbody>{notifs.map((n) => (
        <tr key={n.id} className="border-b last:border-0 hover:bg-muted/40">
          <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">{new Date(n.sent_at.replace(' ', 'T') + 'Z').toLocaleString()}</td>
          <td className="px-4 py-2"><Badge variant="outline" className="capitalize">{n.channel}</Badge></td>
          <td className="px-4 py-2 font-mono text-xs">{n.template}</td>
          <td className="px-4 py-2 text-xs">{n.recipient}</td>
          <td className="px-4 py-2"><Badge variant={n.status === 'sent' ? 'default' : n.status === 'failed' ? 'destructive' : 'outline'}>{n.status}</Badge></td>
        </tr>
      ))}</tbody>
    </table></CardContent></Card>
  );
}

function BillingTab({ subs, passes, memberId, onChange }: { subs: MemberProfile['subscriptions']; passes: MemberProfile['class_passes']; memberId: string; onChange: () => void }) {
  const { getToken } = useAuth();
  const canCancel = usePermission('subscriptions:cancel');
  const [cancelling, setCancelling] = useState(false);

  async function cancelActiveSub() {
    if (!confirm('Cancel this member\'s active Stripe subscription? They keep access until the end of the current period; no further billing.')) return;
    setCancelling(true);
    try {
      const token = await getToken();
      await apiCall(`/v1/stripe/subscriptions/${memberId}/cancel`, { method: 'POST', bearerToken: token });
      onChange();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Cancel failed');
    } finally { setCancelling(false); }
  }

  if (subs.length === 0 && passes.length === 0) {
    return <EmptyCard icon={<CreditCard className="h-8 w-8" />} text="No subscriptions or class passes on file for this member." />;
  }
  const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');
  const available = passes.filter((p) => p.status === 'available').length;
  const used = passes.filter((p) => p.status === 'used').length;
  const activeSub = subs.find((s) => s.status === 'paid' || s.status === 'active' || s.status === 'trialing');
  return (
    <div className="space-y-4">
      {subs.length > 0 && (
        <Card><CardHeader className="pb-2 flex flex-row items-start justify-between space-y-0">
          <CardTitle className="text-base flex items-center gap-2"><CreditCard className="h-4 w-4" /> Subscriptions</CardTitle>
          {activeSub && canCancel && (
            <Button size="sm" variant="ghost" className="h-7 text-destructive hover:text-destructive" onClick={() => void cancelActiveSub()} disabled={cancelling}>
              {cancelling ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Cancel subscription
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0"><table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground border-b"><tr>
            <th className="text-left px-4 py-2 font-medium">Plan</th>
            <th className="text-right px-4 py-2 font-medium">Price</th>
            <th className="text-left px-4 py-2 font-medium">Status</th>
            <th className="text-left px-4 py-2 font-medium">Period end</th>
            <th className="text-left px-4 py-2 font-medium">Started</th>
            <th className="text-left px-4 py-2 font-medium">Source</th>
          </tr></thead>
          <tbody>{subs.map((s) => (
            <tr key={s.id} className="border-b last:border-0 hover:bg-muted/40">
              <td className="px-4 py-2 font-medium">{s.plan_name ?? <span className="text-muted-foreground italic">unnamed</span>}</td>
              <td className="px-4 py-2 text-right tabular-nums">
                {s.plan_amount_cents != null
                  ? <>{formatCents(s.plan_amount_cents)}<span className="text-xs text-muted-foreground">{s.interval ? `/${s.interval}` : ''}</span></>
                  : <span className="text-muted-foreground">—</span>}
              </td>
              <td className="px-4 py-2"><Badge variant={s.status === 'paid' || s.status === 'active' ? 'default' : s.status === 'overdue' ? 'destructive' : 'outline'}>{s.status}</Badge></td>
              <td className="px-4 py-2 text-xs tabular-nums">{fmtDate(s.current_period_end_at)}</td>
              <td className="px-4 py-2 text-xs tabular-nums">{fmtDate(s.started_at)}</td>
              <td className="px-4 py-2 text-xs text-muted-foreground capitalize">{s.billing_partner ?? s.source}</td>
            </tr>
          ))}</tbody>
        </table></CardContent></Card>
      )}
      {passes.length > 0 && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Ticket className="h-4 w-4" /> Class passes <span className="text-xs font-normal text-muted-foreground">({available} available · {used} used)</span></CardTitle></CardHeader>
        <CardContent className="p-0"><table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground border-b"><tr>
            <th className="text-left px-4 py-2 font-medium">Status</th>
            <th className="text-left px-4 py-2 font-medium">Issued</th>
            <th className="text-left px-4 py-2 font-medium">Used</th>
            <th className="text-left px-4 py-2 font-medium">Log</th>
          </tr></thead>
          <tbody>{passes.map((p) => (
            <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
              <td className="px-4 py-2"><Badge variant={p.status === 'available' ? 'default' : 'outline'}>{p.status}</Badge></td>
              <td className="px-4 py-2 text-xs tabular-nums">{fmtDate(p.source_created_at)}</td>
              <td className="px-4 py-2 text-xs tabular-nums text-muted-foreground">{p.used_at ? fmtDate(p.used_at) : '—'}</td>
              <td className="px-4 py-2 text-xs text-muted-foreground truncate max-w-md" title={p.log ?? ''}>{p.log ?? '—'}</td>
            </tr>
          ))}</tbody>
        </table></CardContent></Card>
      )}
    </div>
  );
}

function EmptyHint({ text }: { text: string }) {
  return <p className="text-sm text-muted-foreground py-3 text-center">{text}</p>;
}
function EmptyCard({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
      <div className="mx-auto mb-3 opacity-40">{icon}</div>{text}
    </CardContent></Card>
  );
}

// ── Dialogs ──────────────────────────────────────────────────────────────

function RecordPaymentDialog({ member, onClose, onSaved }: {
  member: MemberProfile['member']; onClose: () => void; onSaved: () => void;
}) {
  const { getToken } = useAuth();
  const onVisitPlan = member.balance_days !== null;
  // Default to whichever model this member already uses. Visit-plan member →
  // "days" model with a sensible default of 30 days. Dues member → covers_month.
  const [mode, setMode] = useState<'days' | 'month'>(onVisitPlan ? 'days' : 'month');
  const defaultAmount = member.monthly_dues_cents > 0 ? (member.monthly_dues_cents / 100).toFixed(2) : '';
  const thisMonth = new Date().toISOString().slice(0, 7);
  const [amount, setAmount] = useState(defaultAmount);
  const [method, setMethod] = useState<'cash' | 'card' | 'transfer' | 'other'>('cash');
  const [coversMonth, setCoversMonth] = useState(thisMonth);
  const [daysAdded, setDaysAdded] = useState('30');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const cents = dollarsToCents(amount);
    if (!Number.isFinite(cents) || cents <= 0) { setErr('Amount must be > $0'); return; }
    let days: number | null = null;
    if (mode === 'days') {
      days = parseInt(daysAdded, 10);
      if (!Number.isFinite(days) || days <= 0) { setErr('Days must be > 0'); return; }
    }
    setBusy(true); setErr(null);
    try {
      const token = await getToken();
      await apiCall('/v1/payments', {
        method: 'POST', bearerToken: token,
        body: {
          member_id: member.id,
          amount_cents: cents, method,
          covers_month: mode === 'month' ? (coversMonth || null) : null,
          days_added:   mode === 'days'  ? days : null,
          notes: notes.trim() || null,
        },
      });
      onSaved();
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Failed'); }
    finally { setBusy(false); }
  }

  const inp = 'w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
  return (
    <Modal title={`Record payment for ${member.name}`} onClose={onClose}>
      <form onSubmit={submit} className="p-5 space-y-3">
        {/* Billing model toggle */}
        <div className="grid grid-cols-2 gap-2 p-1 bg-muted rounded-md">
          <button
            type="button" onClick={() => setMode('days')}
            className={'rounded px-2 py-1.5 text-sm font-medium ' + (mode === 'days' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground')}
          >Adds days</button>
          <button
            type="button" onClick={() => setMode('month')}
            className={'rounded px-2 py-1.5 text-sm font-medium ' + (mode === 'month' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground')}
          >Covers a month</button>
        </div>

        <Field label="Amount (USD) *"><input value={amount} onChange={(e) => setAmount(e.target.value)} required className={inp} placeholder="50.00" /></Field>
        <Field label="Method"><select value={method} onChange={(e) => setMethod(e.target.value as typeof method)} className={inp}>
          <option value="cash">Cash</option><option value="card">Card</option>
          <option value="transfer">Transfer</option><option value="other">Other</option>
        </select></Field>
        {mode === 'days' ? (
          <Field label="Days added">
            <input type="number" min="1" max="3650" value={daysAdded} onChange={(e) => setDaysAdded(e.target.value)} required className={inp} />
            {onVisitPlan && (
              <p className="text-xs text-muted-foreground mt-1">
                Current balance: {member.balance_days} day{member.balance_days === 1 ? '' : 's'}. After this payment: {(member.balance_days ?? 0) + (parseInt(daysAdded, 10) || 0)}.
              </p>
            )}
          </Field>
        ) : (
          <Field label="Covers month"><input type="month" value={coversMonth} onChange={(e) => setCoversMonth(e.target.value)} className={inp} /></Field>
        )}
        <Field label="Notes"><input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} className={inp} /></Field>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Record</Button>
        </div>
      </form>
    </Modal>
  );
}

function EditMemberDialog({ member, onClose, onSaved }: {
  member: MemberProfile['member']; onClose: () => void; onSaved: () => void;
}) {
  const { getToken } = useAuth();
  const [name, setName] = useState(member.name);
  const [email, setEmail] = useState(member.email ?? '');
  const [phone, setPhone] = useState(member.phone ?? '');
  const [duesDollars, setDuesDollars] = useState((member.monthly_dues_cents / 100).toFixed(2));
  const [status, setStatus] = useState<MemberStatus>(member.status);
  const [dob, setDob] = useState(member.date_of_birth ?? '');
  const [usawId, setUsawId] = useState(member.usaw_id ?? '');
  const [usawExpires, setUsawExpires] = useState(member.usaw_expires_on ?? '');
  const [usawType, setUsawType] = useState(member.usaw_membership_type ?? '');
  const [notes, setNotes] = useState(member.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const cents = dollarsToCents(duesDollars);
    if (!Number.isFinite(cents) || cents < 0) { setErr('Invalid dues'); return; }
    setBusy(true); setErr(null);
    try {
      const token = await getToken();
      await apiCall(`/v1/members/${member.id}`, {
        method: 'PATCH', bearerToken: token,
        body: {
          name: name.trim(),
          email: email.trim() || null,
          phone: phone.trim() || null,
          monthly_dues_cents: cents,
          status,
          date_of_birth: dob || null,
          usaw_id: usawId.trim() || null,
          usaw_expires_on: usawExpires || null,
          usaw_membership_type: usawType.trim() || null,
          notes: notes.trim() || null,
        },
      });
      onSaved();
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Failed'); }
    finally { setBusy(false); }
  }

  const inp = 'w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
  return (
    <Modal title="Edit member" onClose={onClose}>
      <form onSubmit={submit} className="p-5 space-y-3">
        <Field label="Name *"><input value={name} onChange={(e) => setName(e.target.value)} required className={inp} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email"><input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className={inp} /></Field>
          <Field label="Phone"><input value={phone} onChange={(e) => setPhone(e.target.value)} className={inp} /></Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Monthly dues (USD)"><input value={duesDollars} onChange={(e) => setDuesDollars(e.target.value)} className={inp} /></Field>
          <Field label="Status"><select value={status} onChange={(e) => setStatus(e.target.value as MemberStatus)} className={inp}>
            <option value="active">Active</option><option value="suspended">Suspended</option><option value="inactive">Inactive</option>
          </select></Field>
          <Field label="Birthday"><input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className={inp} /></Field>
        </div>
        <details className="border rounded-md">
          <summary className="cursor-pointer px-3 py-2 text-xs text-muted-foreground select-none">USA Wrestling membership</summary>
          <div className="p-3 grid grid-cols-3 gap-3">
            <Field label="USAW ID"><input value={usawId} onChange={(e) => setUsawId(e.target.value)} className={inp} /></Field>
            <Field label="Expires"><input type="date" value={usawExpires} onChange={(e) => setUsawExpires(e.target.value)} className={inp} /></Field>
            <Field label="Type"><input value={usawType} onChange={(e) => setUsawType(e.target.value)} placeholder="athlete / coach" className={inp} /></Field>
          </div>
        </details>
        <Field label="Notes"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={inp + ' resize-none'} maxLength={2000} /></Field>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy || !name.trim()}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-card text-card-foreground rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b"><h2 className="text-base font-semibold">{title}</h2></div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>{children}</div>;
}
